import _ from '@/common/helpers';
import PaymentCardBrands from '@/data/payment-card-brands';

/**
 * String identifiers for gateway names that require
 * special configurations
 * @type {{astroPayCard: string, chinaUnionPay: string}}
 */
export const gatewayIdentifiers = {
    astroPayCard: 'AstroPayCard',
    chinaUnionPay: 'MtaPay',
    payPal: 'PayPal',
    intuit: 'Intuit',
    stripe: 'Stripe',
    jeton: 'Jeton',
    gigadat: 'Gigadat',
};

/**
 * Defines special configurations for gateway accounts
 */

/**
 * Gateways that require an onboarding step
 * @type {string[]}
 */
export const onBoardingGateways = [
    gatewayIdentifiers.payPal,
    gatewayIdentifiers.stripe,
    gatewayIdentifiers.intuit,
];

/**
 * Gateways that require a redirect step
 * @type {string[]}
 */
export const redirectStepGateways = [
    gatewayIdentifiers.payPal,
    gatewayIdentifiers.stripe,
];

/**
 * List of gateway configuration fields that
 * require special handling
 * @type {{banks: string}}
 */
export const specialConfigFields = {
    banks: 'banks',
    merchantAccountType: 'merchantAccountType',
    method: 'method',
    payoutMethod: 'payoutMethod',
};

/**
 * Field overrides to handle them differently than defined within
 * the API spec. For example, `forceDcc` is a string field that requires a multiselect.
 */
export function getFieldOverrides({fieldTypes, schema}) {
    function overrideToArray(gatewayName, field) {
        const items = schema[gatewayName].allOf
            .find(v => v.type === 'object')
            .properties.settings.properties[field].enum;
        return {
            type: fieldTypes.array,
            items: items
                .map(method => ({
                    label: _.capitalize(method.toLowerCase()),
                    value: method,
                })),
            placeholder: 'Select a method',
        };
    }
    return {
        [specialConfigFields.banks]: () => ({
            type: fieldTypes.array,
            items: schema.AstroPayBanks.enum
                .map(bank => ({label: bank, value: bank})),
            options: {multiple: true},
            placeholder: 'Select one or more banks',
        }),
        [specialConfigFields.merchantAccountType]: () => ({
            type: fieldTypes.array,
            items: [
                {label: 'E-Commerce', value: '1'},
                {label: 'Mail Order / Telephone order', value: '2'},
                {label: 'Recurring', value: '4'},
            ],
        }),
        [specialConfigFields.method]: ({gatewayName}) => {
            if (gatewayName === gatewayIdentifiers.jeton) {
                return overrideToArray(gatewayName, specialConfigFields.method);
            }
            return {};
        },
        [specialConfigFields.payoutMethod]: ({gatewayName}) => {
            if (gatewayName === gatewayIdentifiers.gigadat) {
                return overrideToArray(gatewayName, specialConfigFields.payoutMethod);
            }
            return {};
        },
    };
}

export const defaultPaymentCardScheme = [
    {brand: PaymentCardBrands.visa, default: true},
    {brand: PaymentCardBrands.mastercard, default: true},
    {brand: PaymentCardBrands.amex, default: true},
    {brand: PaymentCardBrands.discovery, default: true},
    {brand: PaymentCardBrands.maestro, default: false},
    {brand: PaymentCardBrands.solo, default: false},
    {brand: PaymentCardBrands.electron, default: false},
    {brand: PaymentCardBrands.jcb, default: false},
    {brand: PaymentCardBrands.voyager, default: false},
    {brand: PaymentCardBrands.dinersClub, default: false},
    {brand: PaymentCardBrands.switch, default: false},
    {brand: PaymentCardBrands.laser, default: false},
];

export const paymentMethodIdentifiers = {
    paypal: 'paypal',
    paymentCard: 'payment-card',
};

/**
 * Define the payment card schemes for most gateway accounts (default), and for
 * AstroPay Card and China Union Pay
 * @type {{default: Array}}
 */
export const paymentCardSchemes = {
    default: defaultPaymentCardScheme,
    [gatewayIdentifiers.astroPayCard]: [
        {brand: PaymentCardBrands.astroPayCard, default: true},
    ],
    [gatewayIdentifiers.chinaUnionPay]: [
        {brand: PaymentCardBrands.chinaUnionPay, default: true},
    ],
};

/**
 * Limit the method options based on the gateway name value.
 * @type {{}}
 */
export const methodSchemes = {
    [gatewayIdentifiers.payPal]: [paymentMethodIdentifiers.paypal],
};

/**
 * Triggered when the gateway name value changes in the form.
 * @type {{}}
 */
export const gatewayModelOverrides = {
    [gatewayIdentifiers.payPal]: (model) => {
        model.method = paymentMethodIdentifiers.paypal;
    },
};

export const disabledGateways = ['PandaGateway'];
